import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, TrendingUp, Users, Target, ArrowRight, CheckCircle2, Trophy, Building2, Play, Star, Zap, BarChart3, Share2, Rocket, Youtube, Lightbulb, Search, Database, Mail, Eye, ExternalLink, Lock, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { useRealCreators } from '@/hooks/useRealCreators';
import SEO, { PAGE_SEO } from '@/components/SEO';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Animated Counter Component with Intersection Observer
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '', decimals = 0, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);
  
  // Easing function for smooth animation (ease-out-expo)
  const easeOutExpo = useCallback((t) => {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }, []);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Add delay for staggered effect
            setTimeout(() => {
              const startTime = performance.now();
              const startValue = 0;
              const endValue = end;
              
              const animate = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = easeOutExpo(progress);
                const currentValue = startValue + (endValue - startValue) * easedProgress;
                
                setCount(currentValue);
                
                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setCount(endValue);
                }
              };
              
              requestAnimationFrame(animate);
            }, delay);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    
    return () => observer.disconnect();
  }, [end, duration, delay, hasAnimated, easeOutExpo]);
  
  const formattedCount = decimals > 0 
    ? count.toFixed(decimals) 
    : Math.floor(count).toLocaleString();
  
  return (
    <span ref={elementRef} className="tabular-nums">
      {prefix}{formattedCount}{suffix}
    </span>
  );
};

const RealCreatorsSection = () => {
  const navigate = useNavigate();
  // Use shared hook for real YouTube creators
  const {
    creators,
    loading,
    error,
    apiNote,
    lastUpdatedFormatted,
    niches,
    nichesLoading,
    selectedNiche,
    setSelectedNiche,
    refetch
  } = useRealCreators({
    minSubscribers: 10000,
    limit: 12,
    sortBySubscribers: true,
    autoFetch: true
  });

  // Access control for creators section on landing page
  // Public: 6 visible, 3 locked (lighter treatment), max 9
  const LANDING_CREATOR_ACCESS = {
    visible: 6,
    locked: 3,
    maxRender: 9
  };

  return (
    <div>
      {/* Niche Selector */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {nichesLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            Loading niches...
          </div>
        ) : (
          <>
            {/* All Niches option */}
            <button
              onClick={() => setSelectedNiche('All Niches')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedNiche === 'All Niches'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Niches
            </button>
            {/* Dynamic niches from API */}
            {niches.map((niche) => (
              <button
                key={niche}
                onClick={() => setSelectedNiche(niche)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedNiche === niche
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {niche}
              </button>
            ))}
          </>
        )}
      </div>
      
      {/* Creator Count */}
      {!loading && !error && creators.length > 0 && (
        <p className="text-center text-sm text-slate-500 mb-6">
          {creators.length} creators found {selectedNiche !== 'All Niches' ? `in ${selectedNiche}` : ''}
        </p>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading real creators from YouTube...</p>
        </div>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-slate-500">{error}</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Try again
          </button>
        </div>
      )}
      
      {/* Creator Cards with Access Control */}
      {!loading && !error && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Slice creators BEFORE render for performance */}
          {creators.slice(0, LANDING_CREATOR_ACCESS.maxRender).map((creator, index) => {
            // Determine card state: visible or locked
            const isLocked = index >= LANDING_CREATOR_ACCESS.visible;
            // CTA ONLY at index 6 (first locked item)
            const showSignInCTA = index === 6;
            
            return (
              <Card 
                key={creator.channel_id || index} 
                className={`p-6 transition-shadow relative ${isLocked ? 'cursor-not-allowed' : 'hover:shadow-lg'}`}
                style={isLocked ? { 
                  opacity: 0.5,
                  pointerEvents: 'none'
                } : {}}
                tabIndex={isLocked ? -1 : 0}
                data-testid={`real-creator-${index}`}
              >
                {/* Sign-in CTA - ONLY at index 6 */}
                {showSignInCTA && (
                  <div 
                    className="absolute inset-0 bg-white/70 rounded-lg flex flex-col items-center justify-center z-10"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Lock className="w-6 h-6 text-indigo-600 mb-3" />
                    <p className="text-sm text-slate-700 text-center px-4 mb-3 font-medium">
                      Sign in to explore more creators
                    </p>
                    <Button 
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/login?redirect=/brands/discover');
                      }}
                    >
                      Sign in
                    </Button>
                  </div>
                )}
              
                <div className="flex items-start gap-4 mb-4">
                  {creator.thumbnail && (
                    <img 
                      src={creator.thumbnail} 
                      alt={creator.channel_name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-900 truncate" style={{fontFamily: 'Manrope'}}>
                        {creator.channel_name}
                      </h3>
                      {/* Dynamic badge based on data freshness */}
                      {lastUpdatedFormatted ? (
                        <span 
                          className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded cursor-help"
                          title={lastUpdatedFormatted}
                        >
                          {lastUpdatedFormatted.replace('Updated ', '')}
                        </span>
                      ) : (
                        <span 
                          className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded cursor-help"
                          title="Latest creator data"
                        >
                          Latest
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {creator.subscriber_count?.toLocaleString()} subscribers
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Niche</p>
                  <p className="text-sm font-semibold text-indigo-600">{creator.niche}</p>
                </div>
                
                {/* Visit Channel link - hide for locked */}
                {!isLocked && (
                  <a 
                    href={creator.youtube_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Visit Channel
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                
                {/* Locked state text */}
                {isLocked && (
                  <span className="text-sm text-slate-400">Sign in to view</span>
                )}
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !error && creators.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">No creators found for this niche. Try another category.</p>
        </div>
      )}
    </div>
  );
};

export const Landing = () => {
  const navigate = useNavigate();
  const [youtubeLink, setYoutubeLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [selectedNiche, setSelectedNiche] = useState('');
  const [strategyReport, setStrategyReport] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [whatsNewCount, setWhatsNewCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch "What's New" count for navbar
  useEffect(() => {
    const fetchWhatsNewCount = async () => {
      try {
        const res = await axios.get(`${API}/data/whats-new-count`);
        setWhatsNewCount(res.data?.count || 0);
      } catch (error) {
        // Silently fail - not critical
        console.error('Failed to fetch whats new count:', error);
      }
    };
    fetchWhatsNewCount();
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setAuthChecking(true);
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.user_id) {
          setIsLoggedIn(true);
          setCurrentUser(response.data);
          // Cache user data in localStorage for faster subsequent loads
          localStorage.setItem('sponsiq_user', JSON.stringify(response.data));
        } else {
          setIsLoggedIn(false);
          setCurrentUser(null);
          localStorage.removeItem('sponsiq_user');
        }
      } catch (error) {
        // Not logged in - clear any cached data
        setIsLoggedIn(false);
        setCurrentUser(null);
        localStorage.removeItem('sponsiq_user');
      } finally {
        setAuthChecking(false);
      }
    };
    
    // Check for cached user first to prevent flicker
    const cachedUser = localStorage.getItem('sponsiq_user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setIsLoggedIn(true);
        setCurrentUser(userData);
      } catch (e) {
        localStorage.removeItem('sponsiq_user');
      }
    }
    
    // Always verify with backend (will update if session expired)
    checkAuth();
  }, []);

  // Load platform stats on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/platform/stats`);
        setPlatformStats(response.data);
      } catch (error) {
        console.error('Failed to load platform stats:', error);
      }
    };
    loadStats();
  }, []);

  const niches = [
    'Technology & Reviews',
    'Gaming',
    'Beauty & Cosmetics',
    'Fitness & Wellness',
    'Finance & Investing',
    'Food & Cooking',
    'Travel & Vlogs',
    'Education & Learning',
    'Entertainment & Comedy',
    'Music',
    'Fashion & Lifestyle',
    'DIY & Crafts',
    'Business & Entrepreneurship',
    'Health & Wellness',
    'Parenting & Family',
    'Sports',
    'Science & Nature',
    'Art & Design',
    'Other'
  ];

  const generateReport = async () => {
    if (!youtubeLink.trim()) {
      toast.error('Please enter a YouTube channel link');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze/channel`, {
        youtube_link: youtubeLink
      });
      setReport(response.data);
      setShowSignupPrompt(true);
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Failed to analyze channel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatorSignup = () => {
    // Store existing creator mode
    localStorage.setItem('creator_mode', 'existing');
    if (report?.channel_id) {
      localStorage.setItem('pending_channel_id', report.channel_id);
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleStrategySignup = () => {
    // Store strategy mode and niche
    localStorage.setItem('creator_mode', 'strategy');
    localStorage.setItem('strategy_niche', selectedNiche);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGetStarted = () => {
    setShowCreatorModal(true);
  };

  const handleStartStrategy = () => {
    setShowStrategyModal(true);
  };

  const generateStrategyReport = async () => {
    if (!selectedNiche) {
      toast.error('Please select a niche');
      return;
    }

    setStrategyLoading(true);
    setStrategyReport(null);
    
    try {
      const response = await axios.post(`${API}/ai/channel-strategy`, {
        niche: selectedNiche,
        goals: ['Grow to 10k subscribers', 'Get first sponsorship']
      }, {
        timeout: 45000 // 45 second timeout
      });
      setStrategyReport(response.data);
      toast.success('Channel Strategy Report generated!');
    } catch (error) {
      console.error('Strategy generation failed:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate strategy. Please try again.');
      }
    } finally {
      setStrategyLoading(false);
    }
  };

  const handleGetStartedDirect = () => {
    // Clear any stored mode - user is coming fresh
    localStorage.removeItem('creator_mode');
    localStorage.removeItem('strategy_niche');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEO {...PAGE_SEO.home} />
      
      {/* Navigation - Product First */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="home-logo-link">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            <span className="text-lg sm:text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm" data-testid="brands-browse-nav-btn">
                <Database className="w-4 h-4 mr-1.5" />
                Discover Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm" data-testid="creators-discover-nav-btn">
                <Users className="w-4 h-4 mr-1.5" />
                Discover Creators
              </Button>
            </Link>
            {whatsNewCount > 0 && (
              <Link to="/new-this-week">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-600 hover:text-emerald-700 hover:bg-emerald-50"
                  data-testid="whats-new-nav-btn"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  <span className="hidden lg:inline">What's New</span>
                  <Badge 
                    className="ml-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium px-1.5 py-0 h-5 min-w-[20px]"
                    data-testid="whats-new-badge"
                  >
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Button>
              </Link>
            )}
            <Link to="/leaderboards">
              <Button variant="ghost" size="sm" data-testid="leaderboards-nav-btn">
                <Trophy className="w-4 h-4 mr-1.5" />
                Leaderboards
              </Button>
            </Link>
            {!isLoggedIn ? (
              <>
                <Link to="/brands/login">
                  <Button variant="ghost" size="sm" data-testid="brands-nav-btn">
                    <Building2 className="w-4 h-4 mr-1.5" />
                    For Brands
                  </Button>
                </Link>
                <Button onClick={handleGetStartedDirect} variant="outline" size="sm" data-testid="sign-in-nav-btn">
                  Sign In
                </Button>
                <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-9 px-4 rounded-md font-semibold whitespace-nowrap" data-testid="get-started-nav-btn">
                  Analyze Sponsorships
                </Button>
              </>
            ) : (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" data-testid="dashboard-nav-btn">
                    Dashboard
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-9 px-4 rounded-md font-semibold" data-testid="go-to-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-1.5">
            {!isLoggedIn ? (
              <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-8 px-2.5 text-xs rounded-md font-semibold whitespace-nowrap" data-testid="mobile-cta-btn">
                Analyze Sponsorships
              </Button>
            ) : (
              <Link to="/dashboard">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-8 px-2.5 text-xs rounded-md font-semibold whitespace-nowrap">
                  Dashboard
                </Button>
              </Link>
            )}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-slate-700" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              <Link 
                to="/explore-brands" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Database className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Brands</span>
              </Link>
              <Link 
                to="/brands/discover" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Creators</span>
              </Link>
              <Link 
                to="/leaderboards" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Leaderboards</span>
              </Link>
              {whatsNewCount > 0 && (
                <Link 
                  to="/new-this-week" 
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Zap className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">What's New</span>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700 text-xs">
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Link>
              )}
              {!isLoggedIn && (
                <>
                  <Link 
                    to="/brands/login" 
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building2 className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium">For Brands</span>
                  </Link>
                  <div className="pt-2 border-t border-slate-100">
                    <button 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleGetStartedDirect();
                      }}
                      className="flex items-center justify-center w-full px-3 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                    >
                      Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-28">
          <div className="text-center max-w-4xl mx-auto">
            {/* Search Engine Positioning Badge - Not interactive, clearly a badge */}
            <Link to="/explore-brands" className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 text-xs sm:text-sm font-medium mb-4 sm:mb-6 hover:bg-indigo-100 transition-colors cursor-pointer">
              <Database className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Discover brands actively sponsoring creators</span>
              <ArrowRight className="w-3 h-3 flex-shrink-0" />
            </Link>
            
            {/* Hero Heading - Responsive with clamp() */}
            <h1 
              className="font-extrabold tracking-tight text-slate-900 mb-4 sm:mb-6 max-w-3xl mx-auto px-2" 
              style={{
                fontFamily: 'Manrope',
                fontSize: 'clamp(1.75rem, 3vw + 1rem, 3.75rem)',
                lineHeight: '1.1'
              }} 
              data-testid="hero-heading"
            >
              Discover Brands Sponsoring YouTube Creators
            </h1>
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-slate-600 mb-8 sm:mb-12 max-w-2xl mx-auto px-2">
              Explore our database of brands actively investing in creator sponsorships. Find sponsors for your channel or discover which creators brands are working with.
            </p>

            {/* Dual CTA Section */}
            {!report && !strategyReport && (
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="dual-cta-section">
                {/* Existing Creator Path */}
                <div className="bg-white border-2 border-indigo-200 rounded-xl p-6 hover:border-indigo-400 transition-colors" data-testid="existing-creator-cta">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Youtube className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Analyze Sponsorship Activity</h3>
                  <p className="text-sm text-slate-600 mb-4">Discover which brands are sponsoring creators in your niche.</p>
                  <div className="space-y-3">
                    <Input
                      type="text"
                      placeholder="youtube.com/@yourchannel"
                      value={youtubeLink}
                      onChange={(e) => setYoutubeLink(e.target.value)}
                      className="h-11"
                      data-testid="youtube-link-input"
                    />
                    <Button
                      onClick={generateReport}
                      disabled={loading}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 font-semibold"
                      data-testid="analyze-channel-btn"
                    >
                      {loading ? 'Analyzing...' : 'Get Sponsorship Insights'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>

                {/* Aspiring Creator Path */}
                <div className="bg-white border-2 border-emerald-200 rounded-xl p-6 hover:border-emerald-400 transition-colors" data-testid="aspiring-creator-cta">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Browse Brand Database</h3>
                  <p className="text-sm text-slate-600 mb-4">Explore brands actively sponsoring YouTube creators.</p>
                  <div className="space-y-3">
                    <Select value={selectedNiche} onValueChange={setSelectedNiche}>
                      <SelectTrigger className="h-11" data-testid="niche-select">
                        <SelectValue placeholder="Select your niche" />
                      </SelectTrigger>
                      <SelectContent>
                        {niches.map((niche) => (
                          <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={generateStrategyReport}
                      disabled={strategyLoading || !selectedNiche}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 font-semibold"
                      data-testid="start-strategy-btn"
                    >
                      {strategyLoading ? 'Searching...' : 'View Brand Matches'}
                      <Target className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Discover Creators Link */}
            <div className="mt-8 text-center" data-testid="discover-creators-link">
              <Link 
                to="/brands/discover" 
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors"
              >
                <Users className="w-5 h-5" />
                Discover Creators
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Report Preview */}
            {report && (
              <div className="mt-12 bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-left max-w-3xl mx-auto" data-testid="report-preview">
                <div className="mb-6 flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                      {report.channel_name}
                    </h2>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span>Subscribers: <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{report.subscriber_count?.toLocaleString()}</span></span>
                      {report.total_views && (
                        <span>Total Views: <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{report.total_views?.toLocaleString()}</span></span>
                      )}
                      {report.video_count && (
                        <span>Videos: <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{report.video_count?.toLocaleString()}</span></span>
                      )}
                    </div>
                  </div>
                  {report.data_source === 'youtube_api' && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">Live Data</span>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Primary Niche</p>
                    <p className="font-semibold text-slate-900">{report.primary_niche || report.niche}</p>
                    {report.secondary_niches && report.secondary_niches.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Secondary Niches</p>
                        <div className="flex flex-wrap gap-1.5">
                          {report.secondary_niches.map((niche, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                              {niche}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {report.content_style && (
                      <p className="text-sm text-slate-600 mt-2">Content Style: <span className="font-medium">{report.content_style}</span></p>
                    )}
                  </div>
                  {report.upload_frequency && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Upload Frequency</p>
                      <p className="font-semibold text-slate-900">{report.upload_frequency.videos_per_month} videos/month</p>
                      <p className="text-sm text-slate-600 mt-1">Consistency: <span className={`font-medium ${report.upload_frequency.consistency === 'high' ? 'text-green-600' : report.upload_frequency.consistency === 'medium' ? 'text-amber-600' : 'text-slate-600'}`}>{report.upload_frequency.consistency}</span></p>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Sponsorship Likelihood Score</p>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{report.sponsorship_readiness_score}%</div>
                    <div className="flex-1">
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all" style={{width: `${report.sponsorship_readiness_score}%`}}></div>
                      </div>
                      {report.sponsorship_tier && (
                        <p className="text-sm text-slate-600 mt-1">
                          <span className={`font-semibold ${report.sponsorship_tier === 'premium' ? 'text-indigo-600' : report.sponsorship_tier === 'professional' ? 'text-blue-600' : 'text-slate-700'}`}>
                            {report.sponsorship_tier.charAt(0).toUpperCase() + report.sponsorship_tier.slice(1)}
                          </span>
                          {report.tier_description && <span className="text-slate-500"> — {report.tier_description}</span>}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Score Breakdown */}
                {report.score_factors && Object.keys(report.score_factors).length > 0 && (
                  <div className="mb-6 p-4 bg-indigo-50/50 rounded-lg border border-indigo-100">
                    <p className="text-sm font-medium text-slate-700 mb-3">Score Breakdown</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{report.score_factors.subscriber_score || 0}</p>
                        <p className="text-xs text-slate-500">Audience</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{report.score_factors.engagement_score || 0}</p>
                        <p className="text-xs text-slate-500">Engagement</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{report.score_factors.frequency_score || 0}</p>
                        <p className="text-xs text-slate-500">Activity</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {report.recommendations && report.recommendations.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Recommendations</p>
                    <div className="space-y-2">
                      {report.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-amber-500 mt-0.5">💡</span>
                          <div>
                            <span className="font-medium text-slate-700">{rec.area}:</span>
                            <span className="text-slate-600 ml-1">{rec.tip}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detected Brand Partnerships */}
                {report.past_brand_partnerships && report.past_brand_partnerships.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Detected Brand Partnerships</p>
                      <span className="text-xs text-slate-400">{report.videos_analyzed} videos analyzed</span>
                    </div>
                    <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {report.past_brand_partnerships.slice(0, 8).map((partnership, idx) => (
                          <span 
                            key={idx} 
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-200 rounded-full text-sm font-medium text-slate-700"
                            title={`Mentioned ${partnership.mention_count} time(s)`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            {partnership.brand_name}
                            {partnership.mention_count > 1 && (
                              <span className="text-xs text-green-600 font-normal">×{partnership.mention_count}</span>
                            )}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-green-700">
                        <span className="font-semibold">{report.past_brand_partnerships.length} brands detected</span>
                        {' '}— Past partnerships indicate proven sponsorship experience and increase your likelihood score.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Top Brand Matches</p>
                  <div className="space-y-3">
                    {report.top_matches.map((match, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-indigo-50/50 border border-indigo-100 rounded-lg" data-testid={`brand-match-${index}`}>
                        <div>
                          <p className="font-semibold text-slate-900">{match.brand_name}</p>
                          <p className="text-sm text-slate-600">{match.industry}</p>
                        </div>
                        <div className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{match.match_score}%</div>
                      </div>
                    ))}
                  </div>
                </div>

                {showSignupPrompt && (
                  <div className="mt-8 p-6 bg-gradient-to-br from-indigo-50 to-slate-50 rounded-lg border border-indigo-100" data-testid="signup-prompt">
                    <p className="text-slate-900 font-semibold mb-4">
                      Unlock {report.total_matches - 3} more brand matches by creating a free account.
                    </p>
                    <Button onClick={handleCreatorSignup} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 rounded-md font-semibold" data-testid="create-account-btn">
                      Create Free Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Strategy Report Display */}
            {strategyReport && strategyReport.strategy && (
              <div className="mt-12 bg-white border border-emerald-200 rounded-xl shadow-sm p-8 text-left max-w-4xl mx-auto" data-testid="strategy-report">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Rocket className="w-5 h-5 text-emerald-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                        Channel Strategy Report
                      </h2>
                    </div>
                    <p className="text-slate-600">Niche: <span className="font-semibold text-emerald-600">{strategyReport.niche}</span></p>
                  </div>
                  <span className="px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">AI Generated</span>
                </div>

                {/* Channel Positioning */}
                {strategyReport.strategy.channel_positioning && (
                  <div className="mb-6 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-emerald-600" />
                      Channel Positioning
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium text-slate-700">Unique Angle:</span> <span className="text-slate-600">{strategyReport.strategy.channel_positioning.unique_angle}</span></p>
                      <p><span className="font-medium text-slate-700">Value Proposition:</span> <span className="text-slate-600">{strategyReport.strategy.channel_positioning.value_proposition}</span></p>
                      <p><span className="font-medium text-slate-700">Target Viewer:</span> <span className="text-slate-600">{strategyReport.strategy.channel_positioning.target_viewer_profile}</span></p>
                    </div>
                  </div>
                )}

                {/* Content Pillars */}
                {strategyReport.strategy.content_pillars && strategyReport.strategy.content_pillars.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-emerald-600" />
                      Content Pillars
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {strategyReport.strategy.content_pillars.slice(0, 4).map((pillar, idx) => (
                        <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="font-semibold text-slate-900 mb-1">{pillar.pillar_name}</p>
                          <p className="text-sm text-slate-600 mb-2">{pillar.description}</p>
                          <p className="text-xs text-slate-500">Frequency: {pillar.posting_frequency}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monetization Roadmap */}
                {strategyReport.strategy.monetization_roadmap && (
                  <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-600" />
                      Monetization Roadmap
                    </h3>
                    <p className="text-sm text-slate-600 mb-3">{strategyReport.strategy.monetization_roadmap.sponsorship_timeline}</p>
                    {strategyReport.strategy.monetization_roadmap.potential_sponsors && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-slate-500 uppercase mb-2">Potential Sponsors</p>
                        <div className="flex flex-wrap gap-2">
                          {strategyReport.strategy.monetization_roadmap.potential_sponsors.slice(0, 5).map((sponsor, idx) => (
                            <span key={idx} className="px-2 py-1 bg-white border border-amber-200 rounded text-sm font-medium text-slate-700">{sponsor}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {strategyReport.strategy.monetization_roadmap.estimated_rates && (
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="p-2 bg-white rounded">
                          <p className="text-slate-500">At 1K subs</p>
                          <p className="font-semibold text-slate-900">{strategyReport.strategy.monetization_roadmap.estimated_rates.at_1k_subs}</p>
                        </div>
                        <div className="p-2 bg-white rounded">
                          <p className="text-slate-500">At 10K subs</p>
                          <p className="font-semibold text-slate-900">{strategyReport.strategy.monetization_roadmap.estimated_rates.at_10k_subs}</p>
                        </div>
                        <div className="p-2 bg-white rounded">
                          <p className="text-slate-500">At 100K subs</p>
                          <p className="font-semibold text-slate-900">{strategyReport.strategy.monetization_roadmap.estimated_rates.at_100k_subs}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sign Up CTA */}
                <div className="mt-6 p-6 bg-gradient-to-br from-emerald-50 to-slate-50 rounded-lg border border-emerald-100">
                  <p className="text-slate-900 font-semibold mb-4">
                    Create a free account to save your strategy report and track your progress!
                  </p>
                  <Button onClick={handleStrategySignup} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm h-11 px-6 rounded-md font-semibold" data-testid="strategy-signup-btn">
                    Create Free Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How Sponsiq Works - Trust Section */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-slate-900 text-center mb-6" style={{fontFamily: 'Manrope'}} data-testid="how-it-works-heading">
            How Sponsiq Works
          </h2>
          <div className="space-y-3 text-center text-slate-600">
            <p>We analyze YouTube videos to detect real brand sponsorships</p>
            <p>We identify which brands are actively sponsoring creators</p>
            <p>Data is updated continuously as new sponsorships are discovered</p>
          </div>
          
          {/* Social Proof Stats Row */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap justify-center items-center gap-6 md:gap-12 text-sm">
            {/* Always show brands tracked */}
            <div className="flex items-center gap-2 text-slate-600">
              <Database className="w-4 h-4 text-indigo-500" />
              <span><strong className="text-slate-900">{platformStats?.brands_detected || '40'}+</strong> brands tracked</span>
            </div>
            
            {/* Only show creator count if >= 20, otherwise show subtle growing message */}
            {platformStats?.creators_analyzed >= 20 ? (
              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-4 h-4 text-indigo-500" />
                <span><strong className="text-slate-900">{platformStats.creators_analyzed}+</strong> creators analyzed</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-slate-500">
                <Users className="w-4 h-4 text-slate-400" />
                <span>Continuously analyzing creators</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-slate-600">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span>Updated continuously</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Find Your Next Sponsor
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Discover sponsorship opportunities in three simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center" data-testid="how-it-works-step-1">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-indigo-600" />
              </div>
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">Step 1</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>Discover Sponsors</h3>
              <p className="text-slate-600">Browse brands actively sponsoring creators in your niche.</p>
            </div>

            <div className="text-center" data-testid="how-it-works-step-2">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-indigo-600" />
              </div>
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">Step 2</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>Match Brands</h3>
              <p className="text-slate-600">Find brands most likely to sponsor your channel based on niche fit.</p>
            </div>

            <div className="text-center" data-testid="how-it-works-step-3">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-indigo-600" />
              </div>
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full">Step 3</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>Pitch Brands</h3>
              <p className="text-slate-600">Reach out to brands directly with professional pitch templates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section - Live System Metrics */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}} data-testid="social-proof-heading">
              Sponsorship Intelligence Database
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Verified sponsorship data detected from video mentions across YouTube creators</p>
          </div>

          {/* Live System Metrics with Animated Counters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="p-6 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300" data-testid="trust-indicator-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Brands Detected</h3>
              </div>
              {platformStats?.show_real_numbers ? (
                <div className="mb-2">
                  <span className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                    <AnimatedCounter end={platformStats.brands_detected || 0} duration={2000} delay={0} suffix="+" />
                  </span>
                  <p className="text-slate-600 text-sm mt-1">brands in our database</p>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">Brands detected across creator videos.</p>
              )}
            </div>
            
            <div className="p-6 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300" data-testid="trust-indicator-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Play className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Videos Analyzed</h3>
              </div>
              {platformStats?.show_real_numbers ? (
                <div className="mb-2">
                  <span className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                    <AnimatedCounter end={platformStats.videos_analyzed || 5741} duration={2200} delay={100} suffix="+" />
                  </span>
                  <p className="text-slate-600 text-sm mt-1">creator videos scanned</p>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">Creator videos analyzed for sponsors.</p>
              )}
            </div>
            
            <div className="p-6 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300" data-testid="trust-indicator-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Mentions Tracked</h3>
              </div>
              {platformStats?.show_real_numbers ? (
                <div className="mb-2">
                  <span className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                    <AnimatedCounter end={platformStats.total_mentions || 523} duration={1800} delay={200} suffix="+" />
                  </span>
                  <p className="text-slate-600 text-sm mt-1">sponsorship mentions</p>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">Sponsorship mentions tracked.</p>
              )}
            </div>
            
            <div className="p-6 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300" data-testid="trust-indicator-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Active Campaigns</h3>
              </div>
              {platformStats?.show_real_numbers ? (
                <div className="mb-2">
                  <span className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                    <AnimatedCounter end={platformStats.campaigns_created || 0} duration={1600} delay={300} suffix="" />
                  </span>
                  <p className="text-slate-600 text-sm mt-1">campaign opportunities</p>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">Active campaigns from multiple brands.</p>
              )}
            </div>
          </div>

          {/* Real Creators Section */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Real YouTube Creators</h3>
              <p className="text-slate-600">Discover real creators and their sponsorship potential</p>
            </div>

            <RealCreatorsSection />
          </div>
        </div>
      </section>

      {/* AI Report Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}} data-testid="ai-preview-heading">
                See Your AI-Powered Sponsorship Report
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our AI analyzes your channel's performance, content, and audience to generate personalized brand matches with sponsorship probability scores.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Niche detection from video content analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Sponsorship likelihood score based on real metrics</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Past brand partnership detection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Personalized brand match recommendations</span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-12 px-6 rounded-md font-semibold" data-testid="ai-preview-cta">
                Get Your Free Report
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mock Report Preview */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="mock-report-preview">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">TC</div>
                  <div>
                    <p className="font-semibold text-slate-900">Tech Creator Pro</p>
                    <p className="text-sm text-slate-500">125K subscribers</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Sponsorship Score</span>
                    <span className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>87%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full" style={{width: '87%'}}></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase mb-1">Primary Niche</p>
                      <p className="text-sm font-semibold text-slate-900">Technology Reviews</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase mb-1">Tier</p>
                      <p className="text-sm font-semibold text-indigo-600">Professional</p>
                    </div>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 uppercase mb-2">Top Matches</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">Notion • 92%</span>
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">Grammarly • 88%</span>
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">Canva • 85%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creator Value Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Why Creators Use Sponsiq</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">The fastest way to find brands that actually sponsor creators like you</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Discover Active Sponsors</h3>
              <p className="text-slate-600 text-sm">Find brands already sponsoring creators in your niche — not cold leads.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Track Investment Trends</h3>
              <p className="text-slate-600 text-sm">See which companies are actively investing in creator sponsorships.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Generate Instant Pitches</h3>
              <p className="text-slate-600 text-sm">Create professional sponsorship pitch emails with one click.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Track Opportunities</h3>
              <p className="text-slate-600 text-sm">Manage all your sponsorship outreach and deals in one place.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Intelligence Positioning Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 text-sm font-medium mb-6">
                <Database className="w-4 h-4" />
                <span>Sponsorship Intelligence</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>
                A Growing Database of Real Sponsorships
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Sponsiq continuously scans creator content to identify brands sponsoring videos across multiple niches. This creates a growing sponsorship intelligence database that helps creators find the right brands faster.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Detects sponsors from video descriptions and transcripts</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Tracks sponsor frequency and brand popularity</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Updates automatically as new sponsorships are detected</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Covers 12+ niches from Technology to Fitness</span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-12 px-6 rounded-md font-semibold" data-testid="intelligence-cta">
                Search the Database
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Intelligence Preview Card */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Database className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-slate-900">Sponsorship Intelligence Database</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded flex items-center justify-center text-xs font-bold text-indigo-600">db</div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">dbrand</p>
                        <p className="text-xs text-slate-500">Technology</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>153</p>
                      <p className="text-xs text-slate-500">mentions</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center text-xs font-bold text-purple-600">SL</div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Secretlab</p>
                        <p className="text-xs text-slate-500">Gaming</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>87</p>
                      <p className="text-xs text-slate-500">mentions</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center text-xs font-bold text-emerald-600">SQ</div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Squarespace</p>
                        <p className="text-xs text-slate-500">Technology</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>21</p>
                      <p className="text-xs text-slate-500">mentions</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                  <p className="text-xs text-slate-500">Database updates every 12 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-white to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>Start Finding Sponsors Today</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Join creators who use Sponsiq to discover brands actively sponsoring in their niche. Get your free sponsorship report now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-12 px-8 rounded-md font-semibold text-lg" data-testid="cta-get-started-btn">
              Get Your Sponsorship Report
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={handleGetStartedDirect} variant="outline" className="h-12 px-8 rounded-md font-semibold text-lg" data-testid="cta-explore-btn">
              <Search className="w-5 h-5 mr-2" />
              Explore Brand Database
            </Button>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Internal Links & Description */}
      <section className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {/* About Sponsiq */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                About Sponsiq
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Sponsiq is an AI-powered platform that analyzes YouTube sponsorship data to help creators find brand partnerships. 
                We track over 40 actively sponsoring brands across technology, finance, education, and lifestyle niches.
              </p>
            </div>
            
            {/* For Creators */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                For YouTube Creators
              </h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/brands/discover" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Discover Creators Getting Sponsored
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboards" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → View Creator Leaderboards
                  </Link>
                </li>
                <li>
                  <Link to="/explore-brands" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Browse Sponsoring Brands
                  </Link>
                </li>
                <li>
                  <Link to="/resources" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Sponsorship Insights & Guides
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* For Brands */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                For Brands & Marketers
              </h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/brands/discover" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Find YouTube Creators for Sponsorships
                  </Link>
                </li>
                <li>
                  <Link to="/for-brands" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Brand Partnership Solutions
                  </Link>
                </li>
                <li>
                  <Link to="/resources/top-brands-sponsoring-youtube-creators" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Top Sponsoring Brands Report
                  </Link>
                </li>
              </ul>
              <p className="text-sm text-slate-600 mt-4">
                Connect with creators in tech, finance, gaming, beauty, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer showDisclaimer={true} />

      {/* Creator Signup Modal */}
      <Dialog open={showCreatorModal} onOpenChange={setShowCreatorModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Creator Signup
            </DialogTitle>
            <DialogDescription>Get started with your free sponsorship report</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="text-center">
              <p className="text-slate-600 mb-4">Paste your YouTube or Instagram channel link to get started:</p>
              <div className="flex flex-col gap-3">
                <Input
                  type="text"
                  placeholder="https://youtube.com/@yourchannel"
                  value={youtubeLink}
                  onChange={(e) => setYoutubeLink(e.target.value)}
                  className="h-11"
                  data-testid="modal-youtube-input"
                />
                <Button 
                  onClick={() => {
                    setShowCreatorModal(false);
                    generateReport();
                  }}
                  disabled={loading || !youtubeLink.trim()}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11"
                  data-testid="modal-analyze-btn"
                >
                  {loading ? 'Analyzing...' : 'Analyze My Channel'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">or sign in directly</span>
              </div>
            </div>

            <Button 
              onClick={handleCreatorSignup}
              variant="outline"
              className="w-full h-11"
              data-testid="modal-signin-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">How it works:</span>
              </p>
              <ol className="mt-2 text-sm text-slate-600 space-y-1">
                <li>1. Paste your channel link</li>
                <li>2. Get a free sponsorship report preview</li>
                <li>3. Create an account to unlock all brand matches</li>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
